import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import ConfigTemplateNav, { ConfigTemplateSubNav } from './components/ConfigTemplateNav.jsx'
import ConfigTemplateFieldChecklist from './components/ConfigTemplateFieldChecklist.jsx'
import { buildCheckedSetFromConfig, buildFilteredSchema } from './configTemplateSchemaUtils.js'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'

import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions'
import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, componentConfigSchemaErrorSignal } from '../../state/signals/componentConfigSchemaSignals'
import { configTemplateSignal, configTemplateLoadedSignal, configTemplateErrorSignal } from '../../state/signals/configTemplateSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { isEmptyObject, makeFieldsReadonly } from '../../utils/utils'

export default function ConfigTemplateConfig() {

    const navigate = useNavigate()
    const { beId, configTemplateId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate('/')
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => userPermissionsSummary?.value?.canEditConfigTemplates)

    useEffect(() => {
        if (beIdChanged) return
        fetchSchemaManifest()
        fetchConfigTemplate(beId, configTemplateId, config, accessToken).then(() => {
            const version = configTemplateSignal.value?.utgSchemaVersion || '5.0.0.3303'
            fetchComponentConfigSchema(beId, version, config, accessToken)
        })
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    // Initialize checked fields from the current template config when both are loaded
    const configTemplateNameSignal = useSignal('')
    const configTemplateVersionSignal = useSignal('')

    const schemaInitializedSignal = useSignal(false)

    useSignalEffect(() => {
        configTemplateNameSignal.value = configTemplateSignal.value?.name ?? ''
        configTemplateVersionSignal.value = configTemplateSignal.value?.utgSchemaVersion ?? ''
        schemaInitializedSignal.value = false
    })

    useSignalEffect(() => {
        const version = configTemplateVersionSignal.value
        if (!version) return
        if (!schemaInitializedSignal.value) {
            schemaInitializedSignal.value = true
            return
        }
        fetchComponentConfigSchema(beId, version, config, accessToken)
    })

    const schemaVersionOptionsComputed = useComputed(() => {
        const configs = schemaManifestSignal.value?.configs
        if (!configs?.length) return []
        return [...configs]
            .sort((a, b) => a.utgVersion < b.utgVersion ? 1 : -1)
            .map(({ utgVersion }) => (
                <option value={utgVersion} key={utgVersion}>{utgVersion}</option>
            ))
    })

    const handleNameSave = (event) => {
        event.preventDefault()
        updateConfigTemplate(beId, { ...configTemplateSignal.value, name: configTemplateNameSignal.value, utgSchemaVersion: configTemplateVersionSignal.value }, config, accessToken)
            .then(result => {
                configTemplateSignal.value = result
                addNotification('Config template updated.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => {
                addNotification(`Unable to update. ${error.toString()}`, NotificationTypes.ERROR, false)
            })
    }

    const checkedFieldsSignal = useSignal(new Set())

    useSignalEffect(() => {
        if (configTemplateLoadedSignal.value && componentConfigSchemaLoadedSignal.value) {
            try {
                const parsed = JSON.parse(configTemplateSignal.value?.config || '{}')
                checkedFieldsSignal.value = buildCheckedSetFromConfig(parsed)
            } catch {
                checkedFieldsSignal.value = new Set()
            }
        }
    })

    const parsedConfigComputed = useComputed(() => {
        try {
            return JSON.parse(configTemplateSignal.value?.config || '{}')
        } catch {
            return {}
        }
    })

    // Build a filtered schema containing only checked fields, then pass to JsonForm
    const jsonFormDataComputed = useComputed(() => {
        if (!configTemplateLoadedSignal.value || !componentConfigSchemaLoadedSignal.value) return null
        if (isEmptyObject(componentConfigSchemaSignal.value) || isEmptyObject(parsedConfigComputed.value) && checkedFieldsSignal.value.size === 0) {
            if (checkedFieldsSignal.value.size === 0) return null
        }

        let filtered = buildFilteredSchema(componentConfigSchemaSignal.value, checkedFieldsSignal.value)

        if (!canEditComputed.value) {
            filtered = makeFieldsReadonly(filtered)
        }

        globalThis.SCHEMA_MODE = 'CONFIG'

        return {
            schema: filtered.properties?.config,
            value: parsedConfigComputed.value,
            loadedComponentConfig: parsedConfigComputed.value,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditComputed.value
        }
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)

    const handleFormSubmit = values => {
        if (!canEditComputed.value) {
            addNotification('You are not authorized to edit this config template.', NotificationTypes.ERROR)
            return false
        }
        try {
            if (values) {
                const updated = {
                    ...configTemplateSignal.value,
                    name: configTemplateNameSignal.value,
                    utgSchemaVersion: configTemplateVersionSignal.value,
                    config: JSON.stringify(values)
                }
                updateConfigTemplate(beId, updated, config, accessToken)
                    .then(result => {
                        configTemplateSignal.value = result
                        isFormEditedSignal.value = false
                        addNotification('Config template was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        addNotification(`Unable to update the config template. ${error.toString()}`, NotificationTypes.ERROR, false)
                        return false
                    })
            }
        } catch (error) {
            addNotification(`Unable to update the config template. ${error.toString()}`, NotificationTypes.ERROR, false)
        }
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        } else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    const accordionData = [
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'Schema', data: componentConfigSchemaSignal.value },
        { name: 'Checked Fields', data: [...checkedFieldsSignal.value] },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    return (
        <>
            <ConfirmationModal
                show={showCancelModalSignal.value}
                handleCancel={() => showCancelModalSignal.value = false}
                handleConfirm={() => navigate('../')}
                message="You have unsaved changes to this form. Are you sure you want to leave and lose these changes?"
                title="Warning: Unsaved Changes"
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Template List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {configTemplateLoadedSignal.value ? <>Manage {configTemplateSignal.value?.name}</> : <>Loading...</>}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title="Manage Config Template" canEditSignal={canEditComputed} />
            </Stack>
            <Form className="px-3 mb-2" onSubmit={handleNameSave}>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateNameConfig">
                    <Form.Label column sm="2" className="text-end">Name:</Form.Label>
                    <Col sm="10">
                        <Form.Control
                            type="text"
                            required
                            value={configTemplateNameSignal.value}
                            onInput={e => configTemplateNameSignal.value = e.target.value}
                            readOnly={!canEditComputed.value}
                        />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateVersionConfig">
                    <Form.Label column sm="2" className="text-end">Schema Version:</Form.Label>
                    <Col sm="8">
                        <Form.Control as="select" value={configTemplateVersionSignal.value} onChange={e => configTemplateVersionSignal.value = e.target.value} disabled={!canEditComputed.value}>
                            <option value="">Select Schema Version</option>
                            {schemaVersionOptionsComputed.value}
                        </Form.Control>
                    </Col>
                    {canEditComputed.value && (
                        <Col sm="2">
                            <button type="submit" className="btn btn-primary btn-sm">Save</button>
                        </Col>
                    )}
                </Form.Group>
            </Form>
            <ConfigTemplateNav activeKey="2" />
            <ConfigTemplateSubNav activeKey="2" />
            <Loadable
                loadedSignals={[configTemplateLoadedSignal, componentConfigSchemaLoadedSignal]}
                errorSignals={[configTemplateErrorSignal, componentConfigSchemaErrorSignal]}
            >
                <div className="d-flex gap-3 px-3">
                    <div style={{ width: '300px', flexShrink: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                        <ConfigTemplateFieldChecklist
                            schema={componentConfigSchemaSignal.value}
                            checkedFieldsSignal={checkedFieldsSignal}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {checkedFieldsSignal.value.size === 0 ? (
                            <p className="text-muted mt-3">Select fields on the left to include them in this config template.</p>
                        ) : (
                            <JsonForm data={jsonFormDataComputed.value} />
                        )}
                    </div>
                </div>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}
